// OnboardingForm.tsx
// прямые вызовы к API авторизации, без посредников. 
// переходы в машине состояний:
// - регистрация дополнительных данных успешна -> REGISTERED_AND_SET_UP, переход на /dashboard
// - регистрация дополнительных данных неуспешна -> REGISTERED (с ошибкой)
// - отказ от регистрации -> NOT_REGISTERED
import React, { useEffect, useState } from 'react';
import { TextField, Button, MenuItem, Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from "../../store/useAuthStore3";
import { useNavigate } from 'react-router-dom';

interface OnboardingFormProps {
  initial?: {
    legal_name?: string;
    country?: string;
    lang?: string;
  };
  onNavigateToDashboard?: () => void; // Оставляем только роутинг, если он не завязан на стейт-машину
}

const LANGS = [
  { value: 'uk', label: 'Ukrainian' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
];

const OnboardingForm: React.FC<OnboardingFormProps> = ({ initial, onNavigateToDashboard }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Всё общение идет строго через глобальный стор
  const { error: globalError, next: saveOnboardingData, refresh, } = useAuthStore();

  const [legalName, setLegalName] = useState(initial?.legal_name ?? '');
  const [country, setCountry] = useState(initial?.country ?? '');
  const [lang, setLang] = useState(initial?.lang ?? 'en');
  const [isLoading, setIsLoading] = useState(false);

  // Очистка ошибок при размонтировании
  useEffect(() => {
    return () => {
      useAuthStore.setState({ error: null });
    };
  }, []);

  // Отправка формы (ОК)
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    useAuthStore.setState({ error: null });

    await saveOnboardingData({
      user_info: {first_name: legalName,},
      customer_info: {legalName, country, lang, },
    });
    const currentError = useAuthStore.getState().error;
    console.log('[current error in store]', currentError);
    await refresh();
    useAuthStore.setState({ authState: 'REGISTERED_AND_SET_UP' });
    navigate('/points');
  };

  // Отказ от регистрации (Cancel)
  const handleCancel = () => {
    useAuthStore.setState({ authState: 'NOT_REGISTERED' });
  };

  return (
    <Box sx={{ width: 360, mx: "auto", p: 1 }}>
      <Stack spacing={2} component="form" onSubmit={handleSubmit}>
        
        <Typography variant="h5" align="center">
          {t('OnboardingForm.Title')}
        </Typography>
        
        {globalError && (
          <Typography color="error" align="center" sx={{ fontWeight: "bold" }}>
            {globalError}
          </Typography>
        )}

        <TextField
          label={t('OnboardingForm.LegalName')}
          name="legal_name"
          value={legalName}
          onChange={(e) => setLegalName(e.target.value)}
          disabled={isLoading}
          required
          fullWidth
        />

        <TextField
          label={t('OnboardingForm.Country')}
          name="country"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          disabled={isLoading}
          required
          fullWidth
        />

        <TextField
          select
          label={t('OnboardingForm.Language')}
          name="lang"
          value={lang}
          onChange={(e) => setLang(e.target.value)}
          disabled={isLoading}
          fullWidth
        >
          {LANGS.map((l) => (
            <MenuItem key={l.value} value={l.value}>
              {l.label}
            </MenuItem>
          ))}
        </TextField>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
          <Button 
            type="button" 
            onClick={handleCancel} 
            disabled={isLoading}
          >
            {t('OnboardingForm.Cancel')}
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={isLoading}
          >
            {isLoading ? t('OnboardingForm.Saving') : t('OnboardingForm.Ok')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default OnboardingForm;